import React from "react";

export default function PETeacherResumePreview({ componentRef, resumeData = {} }) {
  // --- Helpers ---
  const processHighlighting = (text) => {
    if (!text || typeof text !== "string") return text;
    const parts = text.split(/(\*\*HIGHLIGHT_START\*\*.*?\*\*HIGHLIGHT_END\*\*)/g);
    return parts.map((part, index) => {
      if (part.includes("HIGHLIGHT_START") && part.includes("HIGHLIGHT_END")) {
        const content = part
          .replace(/\*\*HIGHLIGHT_START\*\*/g, "")
          .replace(/\*\*HIGHLIGHT_END\*\*/g, "");
        return (
          <span key={index} style={{ fontWeight: "bold", color: "#000" }}>
            {content}
          </span>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  // --- Safe destructuring with defaults ---
  const {
    isTemplate,
    profile_image_url,
    full_name,
    job_title,
    email,
    phone,
    location,
    linkedin,
    professional_summary,
    technical_skills = [],
    soft_skills = [],
    work_experience = [],
    education = [],
    languages = [],
  } = resumeData;

  // --- Clear, local boolean used in JSX (prevents no-undef) ---
  const shouldShowUserData =
    !isTemplate ||
    !!full_name ||
    !!email ||
    !!phone ||
    !!location ||
    !!professional_summary ||
    technical_skills.length > 0 ||
    soft_skills.length > 0 ||
    work_experience.length > 0 ||
    education.length > 0 ||
    languages.length > 0;

  return (
    <div
      id="section-to-print"
      ref={componentRef}
      className="pe-teacher-resume bg-white shadow-2xl max-w-4xl mx-auto font-sans text-sm print:shadow-none"
    >
      {/* Header */}
      <div className="pe-header bg-gradient-to-r from-purple-900 to-purple-800 text-white p-8 relative">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold mb-2 text-white">
              {full_name || "Hannah Williams"}
            </h1>
            <h2 className="text-xl text-yellow-300 font-medium">
              {shouldShowUserData && job_title
                ? job_title
                : "Middle School Physical Education Teacher"}
            </h2>
          </div>

          {profile_image_url ? (
            <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white shadow-lg">
              <img src={profile_image_url} alt="Profile" className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="header-profile-placeholder w-32 h-32 rounded-full overflow-hidden border-4 border-white shadow-lg">
              <img src="./english.jpeg" alt="Profile" className="w-full h-full object-cover" />
            </div>
          )}
        </div>
      </div>

      <div className="flex">
        {/* Left Sidebar */}
        <div className="pe-sidebar bg-gray-50 w-80 p-6">
          {/* Contact Details */}
          <div className="mb-8">
            <h3 className="text-lg font-bold text-gray-800 mb-4 border-b-2 border-gray-300 pb-2">
              Contact Details
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center">
                <span className="text-orange-500 mr-3">📧</span>
                <span>{email || "support@resumedesign.ai"}</span>
              </div>
              <div className="flex items-center">
                <span className="text-orange-500 mr-3">📞</span>
                <span>{phone || "(555) 345-6789"}</span>
              </div>
              <div className="flex items-center">
                <span className="text-orange-500 mr-3">🏠</span>
                <span>{location || "New York, US"}</span>
              </div>
              <div className="flex items-center">
                <span className="text-orange-500 mr-3">💼</span>
                <span>{linkedin || "LinkedIn.com"}</span>
              </div>
            </div>
          </div>

          {/* Skills */}
          <div className="mb-8">
            <h3 className="text-lg font-bold text-gray-800 mb-4 border-b-2 border-gray-300 pb-2">
              Skills
            </h3>
            <div className="space-y-3">
              {technical_skills.map((skill, index) => (
                <div key={index} className="skill-bar">
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium">{skill}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-yellow-400 to-orange-500 h-2 rounded-full"
                      style={{ width: "85%" }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Soft Skills */}
          <div className="mb-8">
            <h3 className="text-lg font-bold text-gray-800 mb-4 border-b-2 border-gray-300 pb-2">
              Soft Skills
            </h3>
            <div className="space-y-2">
              {soft_skills.map((skill, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className="text-sm">{skill}</span>
                  <div className="flex space-x-1">
                    {[1, 2, 3, 4, 5].map((dot) => (
                      <div
                        key={dot}
                        className={`w-2 h-2 rounded-full ${dot <= 4 ? "bg-orange-400" : "bg-gray-300"}`}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Languages */}
          {languages.length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-bold text-gray-800 mb-4 border-b-2 border-gray-300 pb-2">
                Languages
              </h3>
              <div className="flex flex-wrap gap-2">
                {languages.map((lang, index) => (
                  <span
                    key={index}
                    className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-xs font-medium"
                  >
                    {lang}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8">
          {/* Professional Summary */}
          {professional_summary && (
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-800 mb-4 border-b-2 border-gray-300 pb-2">
                Professional Summary
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {processHighlighting(professional_summary)}
              </p>
            </div>
          )}

          {/* Work Experience */}
          {work_experience.length > 0 && (
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-800 mb-6 border-b-2 border-gray-300 pb-2">
                Work Experience
              </h3>
              <div className="space-y-6">
                {work_experience.map((exp, index) => {
                  const bullets = exp.responsibilities || exp.description_points || [];
                  return (
                    <div key={index}>
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h4 className="font-bold text-orange-600 text-lg">{exp.position}</h4>
                          <p className="text-gray-600 font-medium">{exp.company}</p>
                        </div>
                        <span className="text-sm text-gray-500 font-medium">
                          {exp.start_date} - {exp.end_date}
                        </span>
                      </div>
                      {bullets.length > 0 && (
                        <ul className="list-disc space-y-1 text-gray-700 ml-4" style={{ listStylePosition: 'outside' }}>
                          {bullets.map((resp, respIndex) => (
                            <li key={respIndex} style={{ paddingLeft: 0, marginLeft: 0, display: 'list-item', listStyleType: 'disc', listStylePosition: 'outside' }}>{resp}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Education */}
          {education.length > 0 && (
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-800 mb-6 border-b-2 border-gray-300 pb-2">
                Education
              </h3>
              <div className="space-y-4">
                {education.map((edu, index) => (
                  <div key={index}>
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-bold text-orange-600">{edu.degree}</h4>
                        <p className="text-gray-600">{edu.institution}</p>
                        {edu.description && (
                          <p className="text-gray-700 text-sm mt-1">{edu.description}</p>
                        )}
                      </div>
                      <span className="text-sm text-gray-500">
                        {edu.start_date} - {edu.end_date}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
